<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar movimiento' : 'Nuevo movimiento' }}</h1>
    </div>

    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 860px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? 'Datos del movimiento' : 'Datos del nuevo movimiento' }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">

                <!-- Empleado -->
                <div class="col-md-12" v-if="!editing">
                  <div class="form-group">
                    <label>Empleado <span class="text-danger">*</span></label>
                    <SearchableDropdown
                      :options="employeeOptions"
                      :oldValue="form.employeeId"
                      placeholder="Buscar por nombre o número de documento"
                      @selected="onEmployeeSelected"
                    />
                    <FormError
                      v-if="$v.form.employeeId.$error && !$v.form.employeeId.required"
                      message="El empleado es requerido"
                    />
                  </div>
                </div>

                <!-- Alerta: sin contrato vigente -->
                <div class="col-md-12" v-if="noActiveContract">
                  <div class="alert alert-danger">
                    <i class="ri-error-warning-line mr-2"></i>
                    Este empleado no tiene contrato vigente.
                  </div>
                </div>

                <!-- Alerta: documentación incompleta -->
                <div class="col-md-12" v-if="complianceError">
                  <div class="alert alert-danger">
                    <i class="ri-lock-line mr-2"></i>
                    <strong>Documentación incompleta.</strong> No se puede registrar el movimiento.
                    Documentos faltantes: {{ complianceError.join(', ') }}
                  </div>
                </div>

                <!-- Concepto -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Concepto <span class="text-danger">*</span></label>
                    <SearchableDropdown
                      :options="conceptOptions"
                      :oldValue="form.conceptId"
                      placeholder="Buscar concepto"
                      @selected="onConceptSelected"
                    />
                    <FormError
                      v-if="$v.form.conceptId.$error && !$v.form.conceptId.required"
                      message="El concepto es requerido"
                    />
                    <!-- Concept badge preview -->
                    <div v-if="selectedConcept" class="mt-2">
                      <span :class="`badge ${selectedConcept.type === 'DESCUENTO' ? 'badge-danger' : 'badge-green'} mr-2`">
                        {{ selectedConcept.type === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                      </span>
                      <span class="text-muted small">{{ getNatureLabel(selectedConcept.nature) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Período: Mes -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Mes <span class="text-danger">*</span></label>
                    <FormSelect
                      :items="monthOptions"
                      v-model="form.periodMonth"
                      defaultOption="Seleccione mes"
                      :showError="$v.form.periodMonth.$error && !$v.form.periodMonth.required"
                    />
                    <FormError
                      v-if="$v.form.periodMonth.$error && !$v.form.periodMonth.required"
                      message="El mes es requerido"
                    />
                  </div>
                </div>

                <!-- Período: Año -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Año <span class="text-danger">*</span></label>
                    <FormSelect
                      :items="yearOptions"
                      v-model="form.periodYear"
                      defaultOption="Seleccione año"
                      :showError="$v.form.periodYear.$error && !$v.form.periodYear.required"
                    />
                    <FormError
                      v-if="$v.form.periodYear.$error && !$v.form.periodYear.required"
                      message="El año es requerido"
                    />
                  </div>
                </div>

                <!-- Moneda -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Moneda</label>
                    <FormSelect
                      :items="currencyOptions"
                      v-model="form.currency"
                    />
                  </div>
                </div>

                <!-- Monto total -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="amount">Monto total <span class="text-danger">*</span></label>
                    <input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      v-model="form.amount"
                      autocomplete="off"
                      placeholder="0.00"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.amount.$error }"
                    />
                    <FormError
                      v-if="$v.form.amount.$error && !$v.form.amount.required"
                      message="El monto es requerido"
                    />
                    <FormError
                      v-if="$v.form.amount.$error && !$v.form.amount.positiveAmount"
                      message="El monto debe ser mayor a 0"
                    />
                  </div>
                </div>

                <!-- N° cuotas -->
                <div class="col-md-6" v-if="!editing">
                  <div class="form-group">
                    <label for="totalInstallments">N° cuotas</label>
                    <input
                      id="totalInstallments"
                      type="number"
                      min="1"
                      step="1"
                      v-model="form.totalInstallments"
                      autocomplete="off"
                      placeholder="1"
                      class="custom-form-control"
                    />
                    <small class="text-muted">Dejar en 1 para movimiento puntual</small>
                  </div>
                </div>

                <!-- Cuota estimada + info (solo si cuotas > 1) -->
                <div class="col-md-12" v-if="hasMultipleInstallments && !editing">
                  <div class="row mb-2">
                    <div class="col-md-4">
                      <div class="form-group">
                        <label>Cuota estimada</label>
                        <input
                          type="text"
                          readonly
                          :value="estimatedInstallment"
                          class="custom-form-control bg-light"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="alert alert-info">
                    <i class="ri-information-line mr-1"></i>
                    Se generarán <strong>{{ form.totalInstallments }}</strong> descuentos de
                    <strong>{{ estimatedInstallment }}</strong>
                    desde <strong>{{ startPeriodLabel }}</strong> hasta <strong>{{ endPeriodLabel }}</strong>.
                  </div>
                </div>

                <!-- Descripción -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label for="description">Descripción</label>
                    <textarea
                      id="description"
                      v-model="form.description"
                      rows="3"
                      placeholder="Descripción opcional"
                      class="custom-form-control"
                    ></textarea>
                  </div>
                </div>

                <!-- Sección comprobante (solo creación) -->
                <template v-if="!editing">
                  <div class="col-12"><hr class="my-2" /></div>
                  <div class="col-12">
                    <h6 class="text-muted mb-3">Comprobante (opcional)</h6>
                  </div>

                  <!-- Tipo comprobante -->
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Tipo de comprobante</label>
                      <FormSelect
                        :items="voucherTypeOptions"
                        v-model="form.voucherType"
                        defaultOption="Sin comprobante"
                      />
                    </div>
                  </div>

                  <!-- N° comprobante -->
                  <div class="col-md-4" v-if="form.voucherType">
                    <div class="form-group">
                      <label>N° comprobante</label>
                      <input
                        type="text"
                        v-model="form.voucherNumber"
                        autocomplete="off"
                        placeholder="Ej: B001-05678"
                        class="custom-form-control"
                      />
                    </div>
                  </div>

                  <!-- Archivo adjunto -->
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>Documento adjunto</label>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        class="custom-form-control"
                        @change="onFileChange"
                      />
                      <small class="text-muted">Máx. 5MB</small>
                    </div>
                  </div>
                </template>

              </div>

              <div class="form-group-buttons text-right">
                <router-link :to="{ name: 'payroll-movement-list' }" class="button button-light">
                  {{ editing ? 'Volver' : 'Cancelar' }}
                </router-link>
                <button class="button button-primary" type="submit" :disabled="(noActiveContract || complianceError) && !editing">Guardar</button>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormError, FormSelect, SearchableDropdown } from 'wize-admin'
import { required } from 'vuelidate/lib/validators'
import { PayrollMovementService, PayrollConceptService, EmployeeService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

function positiveAmount(value) {
  return value != null && Number(value) > 0
}

export default {
  name: 'PayrollMovementCreate',
  components: { FormError, FormSelect, SearchableDropdown },

  data() {
    const now = new Date()
    return {
      editing:        false,
      noActiveContract: false,
      complianceError: null,
      employeeOptions: [],
      conceptOptions:  [],
      allConcepts:     [],
      selectedConcept: null,
      selectedFile:    null,
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      currencyOptions: [
        { key: 'PEN', label: 'PEN - Soles' },
        { key: 'USD', label: 'USD - Dólares' },
      ],
      voucherTypeOptions: Object.values(LocalConstants.VoucherType).map(v => ({ key: v.key, label: v.label })),
      form: {
        employeeId:        null,
        conceptId:         null,
        periodMonth:       now.getMonth() + 1,
        periodYear:        now.getFullYear(),
        currency:          'PEN',
        amount:            null,
        description:       null,
        totalInstallments: 1,
        voucherType:       null,
        voucherNumber:     null,
      },
    }
  },

  validations: {
    form: {
      employeeId:  { required },
      conceptId:   { required },
      periodMonth: { required },
      periodYear:  { required },
      amount:      { required, positiveAmount },
    },
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
    hasMultipleInstallments() {
      return Number(this.form.totalInstallments) > 1
    },
    estimatedInstallment() {
      const total = Number(this.form.amount)
      const n = Number(this.form.totalInstallments)
      if (!total || !n || n < 1) return '-'
      const prefix = this.form.currency === 'USD' ? 'US$ ' : 'S/ '
      return prefix + (Math.floor((total / n) * 100) / 100).toFixed(2)
    },
    startPeriodLabel() {
      const m = MONTH_NAMES[(this.form.periodMonth || 1) - 1]
      return `${m} ${this.form.periodYear}`
    },
    endPeriodLabel() {
      const n = Number(this.form.totalInstallments) || 1
      let month = (this.form.periodMonth || 1) + n - 1
      let year = this.form.periodYear || new Date().getFullYear()
      while (month > 12) { month -= 12; year++ }
      return `${MONTH_NAMES[month - 1]} ${year}`
    },
  },

  methods: {
    async onEmployeeSelected(id) {
      this.form.employeeId  = id
      this.noActiveContract = false
      this.complianceError  = null
      if (!id) return
      try {
        const compResp = await EmployeeService.getCompliance(id)
        const p = compResp.payload
        this.complianceError = p.compliant ? null : p.missingDocuments
      } catch (e) { /* non-critical */ }
    },

    onConceptSelected(id) {
      this.form.conceptId   = id
      this.selectedConcept  = this.allConcepts.find(c => c.id === id) || null
    },

    onFileChange(e) {
      this.selectedFile = e.target.files[0] || null
    },

    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },

    async save() {
      this.$v.$touch()
      if (this.$v.$error) return

      try {
        await this.$store.dispatch('app/loading', true)

        if (this.editing) {
          const data = {
            employeeId:  this.form.employeeId,
            conceptId:   this.form.conceptId,
            periodYear:  this.form.periodYear,
            periodMonth: this.form.periodMonth,
            amount:      Number(this.form.amount),
            currency:    this.form.currency,
            description: this.form.description || null,
          }
          await PayrollMovementService.update(this.$route.params.id, data)
        } else {
          const data = {
            employeeId:        this.form.employeeId,
            conceptId:         this.form.conceptId,
            periodYear:        this.form.periodYear,
            periodMonth:       this.form.periodMonth,
            amount:            Number(this.form.amount),
            currency:          this.form.currency,
            description:       this.form.description || undefined,
            totalInstallments: Number(this.form.totalInstallments) || 1,
            voucherType:       this.form.voucherType || undefined,
            voucherNumber:     this.form.voucherNumber || undefined,
          }
          await PayrollMovementService.create(data, this.selectedFile)
        }

        Alert.success('Se almacenó la información correctamente')
        this.$router.push({ name: 'payroll-movement-list' })
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = (await PayrollMovementService.get(id)).payload
        this.form = {
          employeeId:        payload.employeeId,
          conceptId:         payload.conceptId,
          periodMonth:       payload.periodMonth,
          periodYear:        payload.periodYear,
          currency:          payload.currency || 'PEN',
          amount:            payload.amount,
          description:       payload.description,
          totalInstallments: 1,
          voucherType:       null,
          voucherNumber:     null,
        }
        this.selectedConcept = this.allConcepts.find(c => c.id === payload.conceptId) || null
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el movimiento')
        this.$router.push({ name: 'payroll-movement-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    if (this.$route.query.employeeId) {
      this.form.employeeId = Number(this.$route.query.employeeId)
    }

    try {
      const [conceptsResp, employeesResp] = await Promise.all([
        PayrollConceptService.listActive(),
        EmployeeService.listAll(),
      ])
      this.allConcepts    = conceptsResp.payload || []
      this.conceptOptions = this.allConcepts.map(c => ({ key: c.id, label: `${c.code} - ${c.name}` }))
      this.employeeOptions = (employeesResp.payload || []).map(e => ({ key: e.id, label: e.description }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }

    if (this.$route.params.id) {
      this.load(this.$route.params.id)
    }
  },
}
</script>

<style lang="stylus" scoped></style>
